import React, { useCallback, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { getSlidesByDeck } from "../services/slideService";
import { getDeckById } from "../services/deckService";
import { useDeckSession } from "../hooks/useDeckSession";
import { useCanvasFirebase } from "../hooks/useCanvasFirebase";
import CanvasDisplay from "../bigComponents/canvasDisplay";
import throttle from "lodash.throttle";
import { auth } from "../auth/auth";

export default function PresenterEditor() {
  const { deckId } = useParams();
  const [user] = useAuthState(auth);
  const [accessDenied, setAccessDenied] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [slidesList, setSlidesList] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [scale, setScale] = useState(1);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Jog-Wheel State
  const [showJogWheel, setShowJogWheel] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const fadeTimeout = useRef(null);
  const canvasContainerRef = useRef(null);

  const { currentSlideId, updateSession } = useDeckSession(deckId);
  const { elements, setElements } = useCanvasFirebase(currentSlideId, true);

  // 0. Ownership check
  useEffect(() => {
    if (!user || !deckId) return;
    getDeckById(deckId).then((deck) => {
      if (!deck || deck.ownerId !== user.uid) setAccessDenied(true);
      setCheckingAccess(false);
    }).catch(() => { setAccessDenied(true); setCheckingAccess(false); });
  }, [user, deckId]);

  // 1. Handle Scaling
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      if (width < 768) {
        const newScale = (width - 20) / 360; 
        setScale(newScale > 1 ? 1 : newScale);
      } else { setScale(1); }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 2. Load Slides
  useEffect(() => {
    async function loadSlides() {
      if (!deckId || !auth.currentUser) return;
      try {
        const slides = await getSlidesByDeck(deckId, auth.currentUser.uid);
        setSlidesList(slides);
        if (!currentSlideId && slides.length > 0) {
          updateSession({ currentSlideId: slides[0].id });
        }
      } catch (err) { console.error("Failed to load slides:", err); }
    }
    loadSlides();
  }, [deckId, auth.currentUser, currentSlideId, updateSession]);

  // 3. Scroll Sync
  const throttledScroll = useCallback(
    throttle((y) => updateSession({ adminScrollY: y }), 150),
    [updateSession]
  );

  // 4. Jog-Wheel Logic
  const handleJogWheelTouch = (e) => {
    if (!canvasContainerRef.current) return;
    setIsInteracting(true);
    
    // Clear auto-fade timer while touching
    if (fadeTimeout.current) clearTimeout(fadeTimeout.current);

    const touchY = e.touches[0].clientY;
    const centerY = window.innerHeight / 2;
    // Calculate delta from center to accelerate scroll
    const delta = (touchY - centerY) * 0.15; 
    
    canvasContainerRef.current.scrollTop += delta;
  };

  const stopInteraction = () => {
    // Start auto-fade after 2 seconds of no touch
    fadeTimeout.current = setTimeout(() => {
      setIsInteracting(false);
    }, 2000);
  };

  const handleSlideSelect = (id) => {
    updateSession({ currentSlideId: id, adminScrollY: 0 });
    setIsMenuOpen(false);
    if (canvasContainerRef.current) canvasContainerRef.current.scrollTop = 0;
  };

  if (checkingAccess) return <div style={accessScreenStyle}><p style={accessTextStyle}>Verifying access...</p></div>;
  if (accessDenied) return (
    <div style={accessScreenStyle}>
      <div style={accessCardStyle}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
        <h2 style={{ margin: "0 0 8px", color: "#fff", fontSize: "22px" }}>Access Denied</h2>
        <p style={{ margin: 0, color: "#aaa", fontSize: "15px" }}>You don't have permission to present this deck.</p>
      </div>
    </div>
  );

  return (
    <div style={rootStyle}>
      <div style={viewportStyle}>
        
        {/* --- VIRTUAL JOG WHEEL --- */}
        {isMobile && showJogWheel && (
          <div 
            style={{
              ...jogWheelContainer,
              opacity: isInteracting ? 0.8 : 0.2, // Semi-transparent when idle
            }}
            onTouchMove={handleJogWheelTouch}
            onTouchEnd={stopInteraction}
          >
            <div style={jogWheelInner}>
              <div style={jogWheelLabel}>SCROLL</div>
              <div style={jogWheelArrows}>↑<br/>↓</div>
            </div>
          </div>
        )}

        {/* --- MAIN CANVAS --- */}
        <div 
          ref={canvasContainerRef}
          style={{
            ...canvasWrapperBase,
            ...(isMobile ? mobileOverscrollFix : desktopCanvasWrapper),
          }} 
          onScroll={(e) => throttledScroll(e.currentTarget.scrollTop)}
        >
          <div style={{
            transform: `scale(${scale})`,
            transformOrigin: "top center",
            width: "360px",
            margin: "0 auto",
            pointerEvents: "none"
          }}>
            <div style={{ pointerEvents: "auto" }}>
              <CanvasDisplay elements={elements} setElements={setElements} isEditable={false}/>
            </div>
          </div>
        </div>
      </div>

      {/* --- MOBILE FAB MENU --- */}
      {isMobile && (
        <>
          {isMenuOpen && <div style={overlayStyle} onClick={() => setIsMenuOpen(false)} />}
          <div style={fabContainerStyle}>
            <div style={{ 
              ...slideMenuStyles, 
              transform: isMenuOpen ? "scale(1)" : "scale(0)",
              opacity: isMenuOpen ? 1 : 0,
            }}>
               <div style={menuHeader}>CONTROLS</div>
               
               {/* TOGGLE FOR JOG WHEEL */}
               <button 
                  onClick={() => setShowJogWheel(!showJogWheel)}
                  style={{...menuItemStyle, color: showJogWheel ? '#3B00FF' : '#333'}}
               >
                 {showJogWheel ? "● Jog-Wheel ON" : "○ Jog-Wheel OFF"}
               </button>

               <div style={menuHeader}>SLIDES</div>
               <div style={{ overflowY: "auto", flex: 1 }}>
                 {slidesList.map((slide, index) => (
                  <button
                    key={slide.id}
                    onClick={() => handleSlideSelect(slide.id)}
                    style={{
                      ...menuItemStyle,
                      backgroundColor: slide.id === currentSlideId ? "#3B00FF" : "transparent",
                      color: slide.id === currentSlideId ? "white" : "#333",
                    }}
                  >
                    Slide {index + 1}
                  </button>
                ))}
               </div>
            </div>
            <button style={fabButtonStyle} onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? "✕" : "☰"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// --- NEW STYLES ---

const jogWheelContainer = {
  position: "fixed",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "180px",
  height: "180px",
  borderRadius: "50%",
  backgroundColor: "#222",
  border: "4px solid #3B00FF",
  zIndex: 4000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "opacity 0.5s ease",
  boxShadow: "0 0 30px rgba(0,0,0,0.5)",
  touchAction: "none", // Prevent browser from scrolling behind the wheel
};

const jogWheelInner = {
  textAlign: "center",
  color: "white",
  userSelect: "none",
};

const jogWheelLabel = { fontSize: "12px", fontWeight: "bold", letterSpacing: "2px", opacity: 0.6 };
const jogWheelArrows = { fontSize: "32px", marginTop: "5px", color: "#3B00FF" };

const menuHeader = { padding: "10px 15px", color: "#999", fontSize: "10px", fontWeight: "bold", background: "#f9f9f9" };

// --- REUSED BASE STYLES ---
const rootStyle = { display: "flex", height: "100vh", background: "#111", overflow: "hidden", position: "fixed", width: "100%" };
const viewportStyle = { flex: 1, position: "relative", overflow: "hidden", display: "flex", justifyContent: "center" };
const canvasWrapperBase = { height: "100%", overflowY: "auto", background: "white", position: "relative", scrollbarWidth: "none" };
const mobileOverscrollFix = { width: "100vw", overscrollBehaviorY: "contain", WebkitOverflowScrolling: "touch" };
const desktopCanvasWrapper = { width: "360px" };
const fabContainerStyle = { position: "fixed", bottom: "25px", right: "25px", zIndex: 3000, display: "flex", flexDirection: "column", alignItems: "flex-end" };
const fabButtonStyle = { width: "56px", height: "56px", borderRadius: "28px", backgroundColor: "#3B00FF", color: "white", fontSize: "20px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.4)" };
const slideMenuStyles = { backgroundColor: "white", borderRadius: "12px", marginBottom: "12px", width: "160px", maxHeight: "60vh", boxShadow: "0 10px 25px rgba(0,0,0,0.3)", transition: "all 0.2s ease", transformOrigin: "bottom right", display: "flex", flexDirection: "column", overflow: "hidden" };
const menuItemStyle = { width: "100%", padding: "14px", textAlign: "left", border: "none", borderBottom: "1px solid #f0f0f0", fontSize: "14px", fontWeight: "600" };
const overlayStyle = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 2500 };
const accessScreenStyle = { display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#111" };
const accessCardStyle = { textAlign: "center", padding: "40px 32px", background: "#1a1a1a", borderRadius: "16px", border: "1px solid #333" };
const accessTextStyle = { color: "#aaa", fontSize: "16px" };